// SchoolAdminsPanel — super-admin creates/manages per-school admin accounts
// Only visible when logged in as super admin (email verification code)

import { useEffect, useState } from 'react';
import { getSchools, getSchoolAdmins, createSchoolAdmin, updateSchoolAdmin, deleteSchoolAdmin } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { school_id: '', name: '', login_id: '', password: '' };

export default function SchoolAdminsPanel() {
  const [admins,   setAdmins]   = useState([]);
  const [schools,  setSchools]  = useState([]);
  const [form,     setForm]     = useState(EMPTY);
  const [editing,  setEditing]  = useState(null);
  const [filterSchool, setFilter] = useState('');
  const [msg,      setMsg]      = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [a, s] = await Promise.all([getSchoolAdmins(), getSchools()]);
      setAdmins(a); setSchools(s);
    } catch (err) { setMsg('Failed to load: ' + err.message); }
  }

  function startEdit(a) {
    setEditing(a.id);
    setForm({ school_id: a.school_id, name: a.name, login_id: a.login_id, password: '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  async function save(e) {
    e.preventDefault();
    try {
      if (editing) {
        await updateSchoolAdmin(editing, { name: form.name, login_id: form.login_id, password: form.password || undefined });
        setMsg('Admin updated ✅');
      } else {
        await createSchoolAdmin(form);
        setMsg('Admin created ✅');
      }
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function remove(id, name) {
    if (!window.confirm(`Delete admin "${name}"?`)) return;
    try {
      await deleteSchoolAdmin(id);
      load();
    } catch (err) { setMsg('Error: ' + err.message); }
  }

  const filtered = filterSchool
    ? admins.filter(a => String(a.school_id) === filterSchool)
    : admins;

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-clearance mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display font-extrabold text-xl text-ink mb-1">School Admin Accounts</h2>
        <p className="text-sm text-ink-soft">Create named admin accounts for each school. Admins log in with their school code + login ID + password.</p>
      </div>

      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
          {msg}
        </p>
      )}

      <Card className="p-5 mb-6 max-w-[460px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Admin' : 'Create Admin Account'}</h3>

        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>School
            <select required value={form.school_id} onChange={e => setForm({...form, school_id: e.target.value})}
              className={inputCls} disabled={!!editing}>
              <option value="">— Select school —</option>
              {schools.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
            </select>
          </label>

          <label className={labelCls}>Admin Name
            <input required className={inputCls} placeholder="e.g. Principal Kumar"
              value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          </label>

          <label className={labelCls}>Login ID
            <input required className={inputCls} placeholder="e.g. admin.greenwood"
              value={form.login_id} onChange={e => setForm({...form, login_id: e.target.value})} />
          </label>

          <label className={labelCls}>{editing ? 'New Password (leave blank to keep)' : 'Password'}
            <input type="password" required={!editing} className={inputCls} placeholder="Min 6 characters"
              value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
          </label>

          <div className="flex gap-2 mt-1">
            <Button variant="clearance" type="submit">{editing ? 'Update' : 'Create'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
        </form>
      </Card>

      <div className="flex items-center gap-3 mb-3">
        <select value={filterSchool} onChange={e => setFilter(e.target.value)} className={inputCls + ' mt-0'}>
          <option value="">All schools ({admins.length} admins)</option>
          {schools.map(s => (
            <option key={s.id} value={s.id}>
              {s.name} — {admins.filter(a => String(a.school_id) === String(s.id)).length} admin(s)
            </option>
          ))}
        </select>
      </div>

      <Card>
        <CardHeader title={`School Admins (${filtered.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">School</th>
                <th className="px-4 py-2.5">Admin Name</th>
                <th className="px-4 py-2.5">Login ID</th>
                <th className="px-4 py-2.5">Login Instructions</th>
                <th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-t border-line-soft hover:bg-clearance-tint/40">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-ink">{a.school_name}</div>
                    <div className="text-[11px] text-ink-soft">{a.school_code}</div>
                  </td>
                  <td className="px-4 py-3">{a.name}</td>
                  <td className="px-4 py-3"><code className="bg-paper border border-line rounded px-1.5 py-0.5 text-xs">{a.login_id}</code></td>
                  <td className="px-4 py-3 text-xs text-ink-soft leading-relaxed">
                    School Code: <strong className="text-ink">{a.school_code}</strong><br/>
                    Login ID: <strong className="text-ink">{a.login_id}</strong>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(a)}>Edit</Button>
                      <Button variant="danger" className="!px-2.5 !py-1 !text-xs" onClick={() => remove(a.id, a.name)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-ink-soft">No admin accounts yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
